import { Link } from 'react-router-dom'
import PolicyLayout from '../components/PolicyLayout.jsx'

const toc = [
  { id: 'information', label: 'Information we handle' },
  { id: 'cookies', label: 'Cookies' },
  { id: 'use', label: 'How we use information' },
  { id: 'disclosure', label: 'When information is disclosed' },
  { id: 'external', label: 'External links' },
  { id: 'retention', label: 'Data retention' },
  { id: 'security', label: 'Security' },
  { id: 'children', label: 'Children' },
  { id: 'changes', label: 'Policy changes' },
  { id: 'contact', label: 'Contact' },
]

export default function EdgezpacePolicyPage() {
  return (
    <PolicyLayout
      effectiveDate="July 31, 2026"
      footerNote="A stable home for Edgezpace privacy information and public resources."
      label="Public website"
      title="Edgezpace Website Privacy Policy"
      toc={toc}
    >
      <p className="policy-intro">
        This Privacy Policy explains how Edgezpace (&quot;Edgezpace,&quot; &quot;we,&quot;
        &quot;us,&quot; or &quot;our&quot;) handles information when you visit{' '}
        <a href="https://edgezpace.com/">edgezpace.com</a> or contact us about the website.
      </p>

      <section id="information">
        <h2>Information we handle</h2>
        <h3>Information you provide</h3>
        <p>
          The website provides an email link rather than an online contact form. If you email
          us, we receive the information you choose to include, such as your email address,
          name, message, and attachments. Your email provider and ours process that
          communication under their respective terms and policies.
        </p>
        <h3>Technical and network information</h3>
        <p>
          The website is delivered through Cloudflare. Cloudflare may process technical
          information needed to deliver and protect the site, such as IP address, browser and
          device type, requested pages, request time, referring page, approximate network
          location, and security or diagnostic information.
        </p>
        <p>
          The site currently loads fonts from Google Fonts. When your browser requests a font,
          Google may receive technical request information such as your IP address, browser
          information, and the requested resource.
        </p>
        <div className="notice-box">
          <p>
            The reviewed website source does not contain an advertising tracker, marketing
            pixel, user-account system, database-backed contact form, or client-side analytics
            script. If analytics or similar technology is added later, this policy will be
            updated before or when that collection begins.
          </p>
        </div>
      </section>

      <section id="cookies">
        <h2>Cookies and similar technologies</h2>
        <p>
          Edgezpace does not currently set cookies for advertising or marketing. Cloudflare or
          another infrastructure provider may use cookies or comparable technology when
          strictly necessary to deliver, secure, or troubleshoot the site.
        </p>
      </section>

      <section id="use">
        <h2>How we use information</h2>
        <p>We use information to:</p>
        <ul>
          <li>display and operate the portfolio website;</li>
          <li>deliver requested pages and media;</li>
          <li>respond to email and project inquiries;</li>
          <li>diagnose technical problems and maintain site security;</li>
          <li>prevent misuse, fraud, and malicious traffic; and</li>
          <li>comply with applicable legal obligations.</li>
        </ul>
        <p>
          <strong>
            We do not sell personal information and do not use it for third-party advertising.
          </strong>
        </p>
      </section>

      <section id="disclosure">
        <h2>When information is disclosed</h2>
        <p>
          Information may be processed by providers that help operate the website, including
          Cloudflare for hosting, delivery, and security, Google Fonts for font delivery, and
          the email services involved when you contact us. We may also disclose information
          when reasonably necessary to comply with law, protect rights or safety, investigate
          abuse, or defend legal claims.
        </p>
      </section>

      <section id="external">
        <h2>External links</h2>
        <p>
          The portfolio may link to GitHub, deployed projects, social platforms, client
          websites, or other third-party services. Their privacy practices are governed by
          their own policies, and this policy does not cover information collected on those
          external services.
        </p>
      </section>

      <section id="retention">
        <h2>Data retention</h2>
        <p>
          We retain email correspondence for as long as reasonably necessary to respond,
          maintain appropriate business records, resolve disputes, or meet legal obligations.
          Hosting, security, and diagnostic records are retained according to operational needs
          and the practices of the relevant service provider.
        </p>
        <p>
          You may request deletion of personal information contained in correspondence we
          control by emailing <a href="mailto:info@edgezpace.com">info@edgezpace.com</a>. We may
          retain information when required by law or reasonably necessary for security, fraud
          prevention, recordkeeping, or the defense of legal claims.
        </p>
      </section>

      <section id="security">
        <h2>Security</h2>
        <p>
          We use reasonable administrative and technical safeguards designed to protect
          information, including encrypted website connections and Cloudflare security
          controls. No website or method of electronic communication can guarantee absolute
          security.
        </p>
      </section>

      <section id="children">
        <h2>Children</h2>
        <p>
          The Edgezpace portfolio is a general-audience professional website and is not directed
          to children under 13. We do not knowingly collect personal information from children
          under 13 through the website.
        </p>
      </section>

      <section id="changes">
        <h2>Changes to this policy</h2>
        <p>
          We may update this Privacy Policy as the website, its providers, or legal requirements
          change. The revised policy will show a new effective date.
        </p>
      </section>

      <section id="contact">
        <h2>Contact</h2>
        <p>For privacy questions or requests, contact:</p>
        <p>
          <strong>Edgezpace</strong>
          <br />
          <a href="mailto:info@edgezpace.com">info@edgezpace.com</a>
        </p>
        <div className="back-row">
          <Link className="text-link" to="/privacy/">
            Return to the privacy library
          </Link>
        </div>
      </section>
    </PolicyLayout>
  )
}
